import { Handle, Position } from "@xyflow/react";
import { CH_COLORS, effectOf, effLabel } from "@/components/crm/SopFlow";

const H = { width: 11, height: 11, background: "var(--ai)", border: "2px solid var(--panel)" };
const FREE = { width: 10, height: 10, background: "#facc15", border: "2px solid var(--panel)" };

function FreeHandles() {
  return <>
    <Handle type="source" id="free" position={Position.Top} style={{ ...FREE, top: -6 }} title="Drag to draw a free arrow" />
    <Handle type="source" id="free-b" position={Position.Bottom} style={{ ...FREE, bottom: -6 }} title="Drag to draw a free arrow" />
  </>;
}

function OutRow({ sop, k, o, i }) {
  const e = effectOf(sop, k, o);
  const tone = e.type === "close" ? "text-[var(--breach)]" : e.type === "none" ? "text-[var(--text-faint)]" : "text-[var(--text-dim)]";
  return (
    <div className="relative flex items-center gap-2 text-[11px] px-2.5 py-1 border-t border-[var(--border)]" data-testid={`canvas-out-${k}-${i}`}>
      <span className="flex-1 min-w-0 truncate text-[var(--text)]">{o}</span>
      {["none", "next"].includes(e.type) && <span className={`shrink-0 ${tone}`}>{effLabel(sop, k, e)}</span>}
      <Handle type="source" id={o} position={Position.Right} style={{ ...H, right: -6 }} />
    </div>
  );
}

export function TrackNode({ data }) {
  const { sop, k, entry, selected } = data;
  const t = sop.tracks[k];
  return (
    <div className={`w-[270px] rounded-xl border bg-[var(--panel)] shadow-lg transition-colors ${selected ? "border-[var(--ai)]" : entry ? "border-[var(--border-strong)]" : "border-[var(--border)]"}`} data-testid={`canvas-node-${k}`}>
      <FreeHandles />
      <Handle type="target" position={Position.Left} style={{ ...H, left: -6, background: "var(--text-dim)" }} />
      <div className="px-3 pt-2.5 pb-2">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)]">{entry ? "Entry · meeting" : "Track"} · {t.stage}</div>
        <div className="text-sm font-semibold text-[var(--text)] truncate">{t.name || k}</div>
      </div>
      <ol className="px-2.5 pb-2 space-y-1">
        {(t.steps || []).map((s, i) => (
          <li key={i} className="flex items-center gap-2 text-[11px] rounded-md bg-[var(--panel-2)] px-2 py-1">
            <span className="font-mono text-[10px] text-[var(--text-faint)] w-7 shrink-0">D+{s.day}</span>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: CH_COLORS[s.channel] || "#888" }} />
            <span className="flex-1 truncate text-[var(--text)]">{s.label}</span>
          </li>
        ))}
      </ol>
      {t.exit_stage && <div className="px-3 pb-1.5 text-[10px] text-[var(--text-faint)]">After last step: {t.exit_stage} · {t.exit_reason}</div>}
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] px-2.5 pt-1.5 pb-0.5 border-t border-[var(--border)]">Outcomes</div>
      {(sop.outcomes[k] || []).map((o, i) => <OutRow key={o + i} sop={sop} k={k} o={o} i={i} />)}
      {!(sop.outcomes[k] || []).length && <div className="px-2.5 py-1.5 text-[11px] text-[var(--text-faint)]">No outcomes</div>}
    </div>
  );
}

export function ClosedNode({ data }) {
  const { sop, selected } = data;
  return (
    <div className={`w-[220px] rounded-xl border bg-[var(--panel)] ${selected ? "border-[var(--ai)]" : "border-[var(--border)]"}`} data-testid="canvas-node-closed">
      <Handle type="target" position={Position.Left} style={{ ...H, left: -5, background: "var(--text-dim)" }} />
      <div className="px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)]">Lost / Shelved</div><div className="text-sm font-semibold">Revive with</div></div>
      {(sop.outcomes.closed || []).map((o, i) => <OutRow key={o + i} sop={sop} k="closed" o={o} i={i} />)}
    </div>
  );
}

export function NoteNode({ data }) {
  return (
    <div className={`min-w-[160px] max-w-[260px] rounded-lg px-3 py-2 text-xs shadow-lg border-2 ${data.selected ? "border-[var(--text)]" : "border-transparent"}`}
      style={{ background: `color-mix(in srgb, ${data.color} 22%, var(--panel))`, color: "var(--text)" }} data-testid={`canvas-note-${data.id}`}>
      <FreeHandles />
      <Handle type="target" id="in" position={Position.Left} style={{ ...FREE, left: -6 }} />
      <Handle type="source" id="free-r" position={Position.Right} style={{ ...FREE, right: -6 }} />
      <div className="whitespace-pre-wrap break-words leading-relaxed">{data.text || "Note"}</div>
    </div>
  );
}

export function EndNode({ data }) {
  const tone = data.kind === "close" ? "var(--breach)" : "var(--text-dim)";
  return (
    <div className="rounded-full border px-4 py-2 text-xs font-medium bg-[var(--panel)]" style={{ borderColor: `color-mix(in srgb, ${tone} 55%, transparent)`, color: tone }} data-testid={`canvas-end-${data.id}`}>
      <Handle type="target" position={Position.Left} style={{ ...H, left: -5, background: tone }} />
      {data.label}
    </div>
  );
}
